/**
 * CashflowErrorBoundary — Iter 51.0.2 (operational-resilience, 2026-05-15)
 *
 * Lightweight error boundary scoped strictly to the Cashflow Forecast
 * page. Renders a graceful fallback (visible message + Retry) when any
 * downstream component throws during render, instead of crashing the
 * entire React tree to a blank viewport.
 *
 * Intentionally tightly scoped — wraps only the cashflow page.
 * Not a global error boundary; not exported elsewhere; not a redesign.
 */
import { Component } from "react";
import { ArrowsClockwise, Warning } from "@phosphor-icons/react";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";


class CashflowErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Surface to the browser console for operator/devtools visibility.
        // No external logging or analytics — operationally lightweight.
        // eslint-disable-next-line no-console
        console.error("[CashflowErrorBoundary] render failure:", error, info?.componentStack);
    }

    handleRetry = () => {
        // Clearing the boundary's error state re-mounts children, which
        // re-runs the fetch effect inside <CashflowForecast />.
        this.setState({ error: null });
    };

    render() {
        if (!this.state.error) return this.props.children;
        const msg = this.state.error?.message || String(this.state.error) || "Unexpected render error";
        return (
            <div data-testid="cashflow-forecast-boundary-error">
                <PageHeader
                    eyebrow="Cashflow Forecast"
                    title="Expected cashflow — next 12 months"
                    actions={
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={this.handleRetry}
                            className="gap-1.5"
                            data-testid="cashflow-boundary-retry-header"
                        >
                            <ArrowsClockwise size={13} /> Retry
                        </Button>
                    }
                />
                <div className="px-6 sm:px-8 py-8">
                    <EmptyState
                        icon={<Warning size={36} weight="duotone" className="text-rose-500" />}
                        title="Cashflow page hit an unexpected error"
                        description={`${msg}. The rest of the application is unaffected — click Retry below, or refresh the page to reload.`}
                        action={
                            <Button
                                size="sm"
                                onClick={this.handleRetry}
                                className="gap-1.5"
                                data-testid="cashflow-boundary-retry"
                            >
                                <ArrowsClockwise size={13} /> Retry
                            </Button>
                        }
                    />
                </div>
            </div>
        );
    }
}

export default CashflowErrorBoundary;
